import { useEffect, useState } from 'react';
import { KeyRound, Loader2, Ban, RefreshCw } from 'lucide-react';
import {
  fetchMcpConnections, revokeMcpConnection, getDisplayErrorMessage,
  type McpConnection, type McpConnectionList,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  MCP_STATUS_LABEL, mcpApprovedText, mcpLastActivity, mcpRevokeNotice, splitMcpConnections,
} from '@/lib/mcpConnections';
import styles from './McpConnectionsPage.module.css';

/**
 * Connector Access: the claude.ai OAuth connections to this stage's Mcp
 * server (EVII), and Revoke for each. super_admin in the nav, and the Api
 * route (the security boundary) admits super_admin only.
 *
 * On a phone each row stacks as a card with Revoke right under the client
 * (data-label names each cell), so the kill switch is never off-screen.
 *
 * Revoke marks the client revoked and deletes its refresh tokens. The Mcp
 * re-reads the client on every request with a 30 second cache, so the
 * connection loses access within a minute and cannot refresh. Reconnecting
 * goes through the emailed operator sign-in code again.
 */

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function statusClass(status: McpConnection['status']): string {
  if (status === 'connected') return `${styles.badge} ${styles.badgeConnected}`;
  if (status === 'revoked') return `${styles.badge} ${styles.badgeRevoked}`;
  return `${styles.badge} ${styles.badgeIdle}`;
}

export function McpConnectionsPage() {
  useRecentRoute('Connector Access');
  const [list, setList] = useState<McpConnectionList | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState<McpConnection | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setList(await fetchMcpConnections());
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not load the connections.'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const handleRevoke = async (): Promise<void> => {
    if (!confirming) return;
    setRevoking(true);
    setError('');
    try {
      const result = await revokeMcpConnection(confirming.client_id);
      const name = confirming.client_name ?? confirming.client_id;
      setNotice(mcpRevokeNotice(name, result.already_revoked));
      setConfirming(null);
      await load();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not revoke the connection.'));
    } finally {
      setRevoking(false);
    }
  };

  const { live, revoked } = splitMcpConnections(list?.connections ?? []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><KeyRound size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Connector Access</h1>
          <p className={styles.subtitle}>
            Claude connections to the EVII Mcp server. Each one was approved with a sign-in code emailed to the
            operator inbox.
          </p>
        </div>
      </div>

      {error && <div className={styles.alertErr}>{error}</div>}
      {notice && <div className={styles.alertOk}>{notice}</div>}

      <section className={styles.card}>
        <div className={styles.cardTitleRow}>
          <h2 className={styles.cardTitle}>Connections</h2>
          <span className={styles.cacheNote}>A revoke takes effect within a minute</span>
        </div>
        <p className={styles.cardLead}>
          Revoke cuts a connection off and stops it refreshing. To reconnect, start the connection again in
          Claude: it needs a new emailed sign-in code.
        </p>
        <div>
          <button type="button" className={formStyles.btnSecondary} onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 size={13} className={styles.spin} /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>

        {list === null && !error && <p className={styles.loadingText}>Loading...</p>}
        {list !== null && live.length === 0 && <p className={styles.emptyText}>No active connections.</p>}
        {live.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.stack}`}>
              <thead>
                <tr>
                  <th>Client</th><th>Status</th><th>Approved</th><th>Browser</th>
                  <th>Last activity</th><th>Sessions</th><th />
                </tr>
              </thead>
              <tbody>
                {live.map((c) => (
                  <tr key={c.client_id}>
                    <td className={styles.clientCell}>
                      {c.client_name ?? 'Unnamed client'}
                      <span className={styles.sub}>{c.client_id}</span>
                      {c.redirect_hosts.length > 0 && <span className={styles.sub}>Returns to {c.redirect_hosts.join(', ')}</span>}
                    </td>
                    <td data-label="Status"><span className={statusClass(c.status)}>{MCP_STATUS_LABEL[c.status]}</span></td>
                    <td data-label="Approved" className={styles.num}>
                      {mcpApprovedText(c.approved_at, formatDate)}
                      {c.approved_ip && <span className={styles.sub}>IP {c.approved_ip}</span>}
                    </td>
                    <td data-label="Browser"><span className={styles.agent}>{c.approved_user_agent ?? 'Not recorded'}</span></td>
                    <td data-label="Last activity" className={styles.num}>
                      {formatDate(mcpLastActivity(c))}
                      {c.last_refresh_at && <span className={styles.sub}>Refreshed {formatDate(c.last_refresh_at)}</span>}
                    </td>
                    <td data-label="Sessions" className={styles.num}>{c.live_refresh_tokens}</td>
                    <td className={styles.actionCell}>
                      <button
                        type="button"
                        className={styles.revokeBtn}
                        onClick={() => { setNotice(''); setConfirming(c); }}
                        disabled={revoking}
                      >
                        <Ban size={13} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {revoked.length > 0 && (
          <details className={styles.history}>
            <summary>Revoked ({revoked.length})</summary>
            <div className={styles.tableWrap}>
              <table className={`${styles.table} ${styles.stack}`}>
                <thead>
                  <tr><th>Client</th><th>Approved</th><th>Revoked</th><th>Last activity</th></tr>
                </thead>
                <tbody>
                  {revoked.map((c) => (
                    <tr key={c.client_id}>
                      <td className={styles.clientCell}>
                        {c.client_name ?? 'Unnamed client'}
                        <span className={styles.sub}>{c.client_id}</span>
                      </td>
                      <td data-label="Approved" className={styles.num}>
                        {mcpApprovedText(c.approved_at, formatDate)}
                        {c.approved_ip && <span className={styles.sub}>IP {c.approved_ip}</span>}
                      </td>
                      <td data-label="Revoked" className={styles.num}>{formatDate(c.revoked_at)}</td>
                      <td data-label="Last activity" className={styles.num}>{formatDate(mcpLastActivity(c))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        {list !== null && list.unapproved_registrations > 0 && (
          <p className={styles.cardLead}>
            {list.unapproved_registrations} more registration{list.unapproved_registrations === 1 ? '' : 's'} never
            completed a sign-in and hold no access.
          </p>
        )}
      </section>

      <Modal
        isOpen={confirming !== null}
        onClose={() => { if (!revoking) setConfirming(null); }}
        title="Revoke this connection?"
        size="sm"
        footer={
          <>
            <button type="button" className={formStyles.btnSecondary} onClick={() => setConfirming(null)} disabled={revoking}>
              Cancel
            </button>
            <button type="button" className={styles.revokeBtn} onClick={() => void handleRevoke()} disabled={revoking}>
              {revoking ? <Loader2 size={13} className={styles.spin} /> : <Ban size={13} />} Revoke
            </button>
          </>
        }
      >
        <p className={styles.modalText}>
          {confirming?.client_name ?? 'This client'} ({confirming?.client_id}) loses access within a minute and
          cannot refresh.
        </p>
        <p className={styles.modalText}>
          To reconnect, start the connection again in Claude. The operator inbox gets a new sign-in code.
        </p>
      </Modal>
    </div>
  );
}
